import {
  Notification,
  NotificationDocument,
  ServiceStatus,
  ToDoService,
} from '@parkingless/interface';
import { Timestamp } from '@angular/fire/firestore';
import { Garage } from '../interface/garage';

/**
 * The notification bell's logic.
 *
 * Notifications reach the console from two places: the signed-in user's own document,
 * and one document per garage that everyone with a role in that garage shares. They are
 * shown as one list, which is why each entry has to remember where it came from — the
 * previous bell merged them into a bare array and then guessed the source from a
 * `garageId` field that a `Notification` does not have, so acting on a garage
 * notification either did nothing or edited the wrong list.
 *
 * A third kind is not stored anywhere: a job a customer has asked for and the garage has
 * not answered (`pendingServiceEntries`). It is read off the garage itself.
 */

export type NotificationSource =
  | { kind: 'user' }
  | { kind: 'garage'; garageId: string }
  | { kind: 'service'; garageId: string };

export interface NotificationEntry {
  readonly notification: Notification;
  readonly source: NotificationSource;
}

/** Where the bell sends the operator for a job: the agenda, with the job open. */
export function serviceLink(garageId: string, toDoServiceId: string): string {
  return `/parking/${garageId}/service/${toDoServiceId}/view`;
}

/**
 * The jobs customers have asked for and the garage has not answered yet, as entries of
 * the bell.
 *
 * Nothing wrote these. The functions push a new request to the garage's phones and file
 * nothing the console reads, so an operator who works from the browser learnt of one by
 * opening the agenda and reading its subtitle. The garage document the session already
 * listens to has the jobs on it, so the bell reads them there: an entry is there for as
 * long as the job waits, unread, and goes when the job is approved or refused — which is
 * also why it can be neither dismissed nor marked read.
 */
export function pendingServiceEntries(
  garages: readonly Pick<Garage, 'garageId' | 'name' | 'customerList' | 'toDoServiceList'>[],
): NotificationEntry[] {
  const entries: NotificationEntry[] = [];
  for (const garage of garages) {
    for (const service of garage.toDoServiceList ?? []) {
      if (service.status !== ServiceStatus.waitingForGarageApproval) {
        continue;
      }
      const what = [service.service?.serviceName, service.plate].filter(Boolean).join(' · ');
      entries.push({
        notification: {
          notificationId: `service:${garage.garageId}:${service.toDoServiceId}`,
          isRead: false,
          title: 'Servizio da confermare',
          body: [
            serviceCustomerName(service, garage.customerList),
            what,
            // Whose it is, for someone who works in more than one garage.
            garages.length > 1 ? garage.name : '',
          ]
            .filter(Boolean)
            .join(' — '),
          data: {
            garageId: garage.garageId,
            actionList: [
              { buttonName: 'Apri', action: serviceLink(garage.garageId, service.toDoServiceId) },
            ],
          },
          creationDate:
            service.insertDate ?? service.createdAt ?? service.lastUpdate ?? service.serviceDesiredDate,
        },
        source: { kind: 'service', garageId: garage.garageId },
      });
    }
  }

  return entries;
}

/** Who a job is for: the walk-in named on it, else the garage's customer. */
function serviceCustomerName(
  service: ToDoService,
  customers: Garage['customerList'] | undefined,
): string {
  if (service.externalCustomer) {
    return `${service.externalCustomer.name ?? ''} ${service.externalCustomer.surname ?? ''}`.trim();
  }
  const customer = (customers ?? []).find((entry) => entry.customerId === service.customerId);

  return customer ? `${customer.surname ?? ''} ${customer.name ?? ''}`.trim() : '';
}

/** What the bell has already seen waiting: which garages, and which jobs in them. */
export interface PendingSeen {
  readonly garageIds: ReadonlySet<string>;
  readonly notificationIds: ReadonlySet<string>;
}

/**
 * The requests that have arrived since the bell last looked, and what it has seen now.
 *
 * Only a job that turns up in a garage already being watched is news. What was waiting
 * when the console opened, and everything in a garage that has only just loaded, is
 * what the badge is for — a toast for each would greet every sign-in with a stack of
 * them.
 */
export function newPendingServices(
  seen: PendingSeen | null,
  garageIds: readonly string[],
  pending: readonly NotificationEntry[],
): { fresh: NotificationEntry[]; seen: PendingSeen } {
  const fresh = seen
    ? pending.filter(
        (entry) =>
          entry.source.kind === 'service' &&
          seen.garageIds.has(entry.source.garageId) &&
          !seen.notificationIds.has(entry.notification.notificationId),
      )
    : [];

  return {
    fresh,
    seen: {
      garageIds: new Set(garageIds),
      notificationIds: new Set(pending.map((entry) => entry.notification.notificationId)),
    },
  };
}

/**
 * The user's own notifications and every garage's, newest first, with the jobs waiting
 * for an answer among them.
 */
export function mergeNotifications(
  userNotifications: readonly Notification[] | undefined,
  garageDocuments: readonly NotificationDocument[] | undefined,
  pending: readonly NotificationEntry[] = [],
): NotificationEntry[] {
  const entries: NotificationEntry[] = [
    ...pending,
    ...(userNotifications ?? []).map((notification) => ({
      notification,
      source: { kind: 'user' as const },
    })),
  ];

  for (const document of garageDocuments ?? []) {
    for (const notification of document.notificationList ?? []) {
      entries.push({
        notification,
        source: { kind: 'garage' as const, garageId: document.garageId },
      });
    }
  }

  return entries.sort(
    (a, b) =>
      (b.notification.creationDate?.seconds ?? 0) - (a.notification.creationDate?.seconds ?? 0),
  );
}

export function unreadCount(entries: readonly NotificationEntry[]): number {
  return entries.filter((entry) => !entry.notification.isRead).length;
}

/** The unread ones "Segna come lette" can do something about: not a job still waiting. */
export function markableCount(entries: readonly NotificationEntry[]): number {
  return unreadCount(entries.filter((entry) => entry.source.kind !== 'service'));
}

/** A copy of the notification marked read, stamped with when. */
export function readNotification(notification: Notification, now: Date): Notification {
  return { ...notification, isRead: true, readDate: Timestamp.fromDate(now) };
}

/** Every notification in the list marked read; unchanged ones are left alone. */
export function markAllRead(list: readonly Notification[] | undefined, now: Date): Notification[] {
  return (list ?? []).map((notification) =>
    notification.isRead ? notification : readNotification(notification, now),
  );
}

/** The list with one notification replaced. */
export function replaceNotification(
  list: readonly Notification[] | undefined,
  notification: Notification,
): Notification[] {
  return (list ?? []).map((entry) =>
    entry.notificationId === notification.notificationId ? notification : entry,
  );
}

/** The list without one notification. */
export function removeNotification(
  list: readonly Notification[] | undefined,
  notificationId: string,
): Notification[] {
  return (list ?? []).filter((entry) => entry.notificationId !== notificationId);
}

/**
 * What a notification's action button should do.
 *
 * Buttons carry an `action` that is either a route to open or, for the service-approval
 * notifications the customer apps raise, a decision to record on the notification itself.
 */
export type NotificationOutcome =
  { kind: 'navigate'; path: string } | { kind: 'decide'; status: string };

export function outcomeOf(notification: Notification, action: string): NotificationOutcome {
  const verb = action.split('/').pop() ?? '';
  const isServiceDecision = !!notification.data?.serviceId && verb !== 'edit';

  if (!isServiceDecision) {
    return { kind: 'navigate', path: action };
  }

  // 'rejectedFromGarage' is the ServiceStatus value; the previous console wrote
  // 'rejectedfromGarage', which matches no status the other apps recognise.
  return { kind: 'decide', status: verb === 'accept' ? 'approved' : 'rejectedFromGarage' };
}

/** A copy of the notification with the decision recorded on it, and marked read. */
export function decidedNotification(
  notification: Notification,
  status: string,
  now: Date,
): Notification {
  return {
    ...readNotification(notification, now),
    data: { ...(notification.data ?? {}), status },
  };
}
